/**
 * Events page helpers (#50): merge legacy seasons with the new events collection,
 * build event documents from the create/edit form, and format dates, ranks and
 * progress hints for the Events UI. Ranking itself always comes from eventsEngine.
 */

import {
  normalizeEvent,
  migrateLegacyEvent,
  toDayKey,
  getEventStandings,
  getEventWinner,
  isSessionInEvent,
} from './eventsEngine';
import { CRITERIA_CATALOGUE, CRITERIA_GROUPS, formatStatValue, getCriterion } from './eventCriteria';

export const MAX_TIE_BREAKERS = 3;

// Short column headers for the leaderboard; the full label goes in the <abbr> title.
const SHORT_LABELS = {
  matchWins: 'W',
  matchWinPct: 'Win %',
  matchesPlayed: 'MP',
  mvps: 'MVP',
  gameWins: 'GW',
  gameWinPct: 'GW %',
  gameDiff: '+/-',
  gamesPlayed: 'GP',
  holdWins: 'Holds',
  holdPct: 'Hold %',
  breakWins: 'Breaks',
  breakPct: 'Break %',
  goldenPointWins: 'Gold pts',
  loveGameWins: 'Love',
  setWins: 'Sets',
  setDiff: 'Set +/-',
  tiebreakWins: 'TBs',
  longestMatchStreak: 'Streak',
  longestGameStreak: 'Game streak',
};

export const getShortLabel = (statId) => SHORT_LABELS[statId] || getCriterion(statId).label;

// Leaderboard (Figma 788:17017): three columns, the criterion first, then W / L / %
// without repeating it. Losses are not a ranking criterion, so they get their own id.
const BOARD_DEFAULTS = ['matchWins', 'matchLosses', 'matchWinPct'];
const BOARD_LABELS = { matchWins: 'W', matchLosses: 'L', matchWinPct: '%' };
const BOARD_TITLES = { matchLosses: 'Match losses' };

export function getBoardColumns(event) {
  const ids = [event?.criterion?.stat || 'matchWins', ...BOARD_DEFAULTS];
  return ids
    .filter((id, i) => ids.indexOf(id) === i)
    .slice(0, 3)
    .map((id) => ({
      id,
      label: BOARD_LABELS[id] || getShortLabel(id),
      title: BOARD_TITLES[id] || getCriterion(id).label,
    }));
}

export function getBoardCell(row, statId) {
  if (statId === 'matchLosses') return String(row.stats?.l ?? 0);
  const crit = getCriterion(statId);
  const value = crit.compute(row.stats || {});
  // Percentages to one decimal place, as in the designs (71.4%)
  if (crit.isRatio) return `${((value ?? 0) * 100).toFixed(1)}%`;
  return formatStatValue(statId, value);
}

// Event type presets (Figma 791:18654). "Custom" is whatever does not match one of these.
export const EVENT_TEMPLATES = [
  {
    id: 'league',
    name: 'League',
    blurb: 'Most match wins',
    icon: 'list',
    criterion: 'matchWins',
    tieBreakers: ['matchWinPct', 'gameDiff'],
  },
  {
    id: 'real-mvp',
    name: 'Real MVP',
    blurb: 'Most MVP wins',
    icon: 'social_leaderboard',
    criterion: 'mvps',
    tieBreakers: ['matchWins', 'matchWinPct'],
  },
  {
    id: 'hold-serve',
    name: 'Hold your serve',
    blurb: 'Most serve holds',
    icon: 'padel',
    criterion: 'holdWins',
    tieBreakers: ['holdPct', 'gameWins'],
  },
  {
    id: 'deuce-master',
    name: 'Deuce master',
    blurb: 'Most deuce games won',
    icon: 'crown',
    criterion: 'goldenPointWins',
    tieBreakers: ['gameWins', 'matchWins'],
  },
  {
    id: 'longevity',
    name: 'Longevity',
    blurb: 'Most matches played',
    icon: 'exercise',
    criterion: 'matchesPlayed',
    tieBreakers: ['matchWins', 'gamesPlayed'],
  },
];

/** The preset a criterion + tie-breakers pair matches exactly, or null for Custom. */
export function findTemplate(criterion, tieBreakers = []) {
  return EVENT_TEMPLATES.find((t) => t.criterion === criterion
    && t.tieBreakers.length === tieBreakers.length
    && t.tieBreakers.every((tb, i) => tb === tieBreakers[i])) || null;
}

// Order from the designs: Both, Classic, MVP
export const MODE_OPTIONS = [
  { value: 'both', label: 'Both', modes: ['rotation', 'classic'] },
  { value: 'classic', label: 'Classic', modes: ['classic'] },
  { value: 'rotation', label: 'MVP', modes: ['rotation'] },
];

export const modesToOption = (modes = []) => {
  const hasRotation = modes.includes('rotation');
  const hasClassic = modes.includes('classic');
  if (hasRotation && !hasClassic) return 'rotation';
  if (hasClassic && !hasRotation) return 'classic';
  return 'both';
};

export const describeModes = (modes = []) => {
  const option = modesToOption(modes);
  if (option === 'rotation') return 'MVP matches';
  if (option === 'classic') return 'Classic matches';
  return 'MVP + Classic';
};

/** Criteria that make sense for the chosen modes, grouped in catalogue order. */
export function getCriteriaGroups(modes = ['rotation', 'classic']) {
  const all = Object.values(CRITERIA_CATALOGUE).filter((c) => c.modes.some((m) => modes.includes(m)));
  return CRITERIA_GROUPS
    .map((group) => ({ group, criteria: all.filter((c) => c.group === group) }))
    .filter((g) => g.criteria.length > 0);
}

// Legacy seasons froze `eventMvp` as the old matchStats winner object
// ({ player, tiedPlayers, ... }); turn it into the engine's winner shape.
function withLegacyWinner(doc) {
  const mvp = doc.eventMvp;
  if (doc.winner || !mvp || typeof mvp !== 'object') return doc;
  const players = Array.isArray(mvp.tiedPlayers) && mvp.tiedPlayers.length > 0
    ? mvp.tiedPlayers
    : (mvp.player && mvp.player !== 'Tie' ? [mvp.player] : []);
  const { eventMvp, ...rest } = doc;
  if (players.length === 0) return rest;
  return {
    ...rest,
    winner: { player: players.length === 1 ? players[0] : 'Tie', players, decidedAt: doc.endTimestamp || null },
  };
}

// Winner names must be strings; older migrated docs can hold the legacy object inside.
function cleanWinner(winner) {
  if (!winner) return null;
  const players = (winner.players || [])
    .flatMap((p) => (p && typeof p === 'object' ? (p.tiedPlayers || [p.player]) : [p]))
    .filter((p) => typeof p === 'string' && p && p !== 'Tie');
  if (players.length === 0) return null;
  return { ...winner, player: players.length === 1 ? players[0] : 'Tie', players };
}

/**
 * Merge the legacy `seasons` collection with the new `events` collection.
 * An events doc wins over a season with the same id (it is that season, migrated),
 * except that the season's sessionIds stay authoritative: archiving still appends there.
 */
export function mergeEventSources(seasonDocs = [], eventDocs = []) {
  const byId = new Map();

  seasonDocs.forEach((season) => {
    const migrated = migrateLegacyEvent(withLegacyWinner(season));
    byId.set(season.id, { ...migrated, winner: cleanWinner(migrated.winner), id: season.id, isLegacySeason: true, hasEventDoc: false });
  });

  eventDocs.forEach((ev) => {
    const legacy = byId.get(ev.id);
    const sessionIds = legacy && Array.isArray(legacy.sessionIds) ? legacy.sessionIds : ev.sessionIds;
    const normalized = normalizeEvent(withLegacyWinner({ ...ev, sessionIds }));
    byId.set(ev.id, {
      ...normalized,
      winner: cleanWinner(normalized.winner),
      id: ev.id,
      isLegacySeason: Boolean(legacy),
      hasEventDoc: true,
    });
  });

  return Array.from(byId.values()).sort((a, b) => {
    if (a.startDate !== b.startDate) return a.startDate < b.startDate ? 1 : -1;
    return (b.createdAt || 0) - (a.createdAt || 0);
  });
}

/** The legacy season that still collects new sessions by id, if any. */
export function getActiveLegacySeasonId(events = []) {
  const target = events.find((e) => e.isLegacySeason && e.status === 'active' && Array.isArray(e.sessionIds));
  return target ? target.id : null;
}

/** Standings and winner for every event, keyed by id. */
export function buildEventSummaries(events = [], archivedSessions = []) {
  const summaries = {};
  events.forEach((event) => {
    const standings = getEventStandings(event, archivedSessions, []);
    const winner = getEventWinner(event, standings);
    // A rotation session and a classic match each count as one match
    const matchesPlayed = archivedSessions.filter((s) => isSessionInEvent(s, event)).length;
    summaries[event.id] = { standings, winner, matchesPlayed };
  });
  return summaries;
}

/** Ended events with a winner (frozen or computed), for Hall of Fame counts. */
export function withResolvedWinners(events = [], summaries = {}) {
  return events.map((event) => {
    if (event.status !== 'ended' || event.winner) return event;
    const computed = summaries[event.id]?.winner;
    return computed ? { ...event, winner: computed } : event;
  });
}

const today = () => toDayKey(Date.now());

const parseDay = (dayKey) => {
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const daysBetween = (fromKey, toKey) => Math.round((parseDay(toKey) - parseDay(fromKey)) / 86400000);

export function formatDay(dayKey, { withYear = false } = {}) {
  if (!dayKey) return '';
  const opts = { day: 'numeric', month: 'short' };
  if (withYear) opts.year = 'numeric';
  return parseDay(dayKey).toLocaleDateString('en-GB', opts);
}

export function formatDateRange(event) {
  const start = event.startDate;
  const end = event.endDate;
  const thisYear = String(new Date().getFullYear());
  const withYear = !start.startsWith(thisYear) || (end && !end.startsWith(thisYear));
  if (!end) return `From ${formatDay(start, { withYear })}`;
  if (start === end) return formatDay(start, { withYear });
  return `${formatDay(start, { withYear })} – ${formatDay(end, { withYear })}`;
}

/** One-line progress hint for an event card. */
export function getProgressHint(event, matchesPlayed = 0, now = today()) {
  const matchesText = `${matchesPlayed} ${matchesPlayed === 1 ? 'match' : 'matches'} played`;
  if (event.status === 'ended') {
    const endKey = event.endDate || toDayKey(event.endTimestamp) || now;
    return `Ended ${formatDay(endKey)} • ${matchesText}`;
  }
  if (event.startDate > now) {
    const days = daysBetween(now, event.startDate);
    return days === 1 ? 'Starts tomorrow' : `Starts in ${days} days`;
  }
  if (event.endDate) {
    const days = daysBetween(now, event.endDate);
    const left = days === 0 ? 'Ends today' : days === 1 ? '1 day left' : `${days} days left`;
    return `${left} • ${matchesText}`;
  }
  return matchesText;
}

/** Progress through a dated event, 0..1, or null for open-ended events. */
export function getProgressFraction(event, now = today()) {
  if (event.status === 'ended') return 1;
  if (!event.endDate) return null;
  const total = daysBetween(event.startDate, event.endDate) + 1;
  const done = daysBetween(event.startDate, now) + 1;
  if (total <= 0) return 1;
  return Math.max(0, Math.min(1, done / total));
}

export const formatRank = (row) => {
  if (!row || row.rank == null) return '–';
  return row.isTied ? `=${row.rank}` : String(row.rank);
};

export function describeCriterion(event) {
  const crit = getCriterion(event.criterion?.stat);
  return crit.direction === 'asc' ? `Fewest ${crit.label.toLowerCase()}` : `Most ${crit.label.toLowerCase()}`;
}

/** Empty form state for a new event. */
export function getEmptyEventForm() {
  return {
    name: '',
    description: '',
    startDate: today(),
    endDate: '',
    modes: ['rotation', 'classic'],
    participants: 'all',
    criterion: 'matchWins',
    tieBreakers: ['matchWinPct', 'gameDiff'],
    minMatches: '',
  };
}

/** Form state from an existing event. */
export function eventToForm(event) {
  const ev = normalizeEvent(event);
  return {
    name: ev.name || '',
    description: ev.description || '',
    startDate: ev.startDate || today(),
    endDate: ev.endDate || '',
    modes: ev.modes,
    participants: ev.participants,
    criterion: ev.criterion.stat,
    tieBreakers: ev.tieBreakers.map((tb) => tb.stat),
    minMatches: ev.minMatches ? String(ev.minMatches) : '',
  };
}

/**
 * Firestore fields for an event from the form. Directions come from the catalogue.
 * Every value is defined (Firestore rejects undefined).
 */
export function formToEventFields(form) {
  const minMatches = parseInt(form.minMatches, 10);
  return {
    name: form.name.trim(),
    description: (form.description || '').trim(),
    startDate: form.startDate || today(),
    endDate: form.endDate || null,
    modes: form.modes && form.modes.length > 0 ? form.modes : ['rotation', 'classic'],
    participants: Array.isArray(form.participants) ? form.participants : 'all',
    criterion: { stat: form.criterion, direction: getCriterion(form.criterion).direction || 'desc' },
    tieBreakers: (form.tieBreakers || [])
      .filter(Boolean)
      .slice(0, MAX_TIE_BREAKERS)
      .map((stat) => ({ stat, direction: getCriterion(stat).direction || 'desc' })),
    minMatches: Number.isFinite(minMatches) && minMatches > 0 ? minMatches : 0,
  };
}

/** Problems that block saving the form, in display order. */
export function validateEventForm(form) {
  const errors = [];
  if (!form.name.trim()) errors.push('Give the event a name.');
  if (form.endDate && form.startDate && form.endDate < form.startDate) errors.push('The end date is before the start date.');
  if (Array.isArray(form.participants) && form.participants.length < 2) errors.push('Pick at least 2 players.');
  return errors;
}
